import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div<{ $color: string }>`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const StatIcon = styled.div<{ $bg: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${p => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
`;

const Section = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const RoleBadge = styled.span<{ $role: string }>`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${p => 
    p.$role === 'SUPERADMIN' ? 'rgba(239, 68, 68, 0.1)' :
    p.$role === 'ADMIN' ? 'rgba(59, 130, 246, 0.1)' :
    'rgba(100, 116, 139, 0.1)'
  };
  color: ${p => 
    p.$role === 'SUPERADMIN' ? '#dc2626' :
    p.$role === 'ADMIN' ? '#2563eb' :
    '#64748b'
  };
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;
`;

interface DashboardData {
  stats: {
    totalUsers: number;
    totalGames: number;
    totalScores: number;
    totalDongeng: number;
  };
  qrisStats: {
    total: number;
    paid: number;
    pending: number;
    expired: number;
    failed: number;
    revenue: number;
    successRate: number;
  };
  recentUsers: Array<{
    _id: string;
    name: string;
    email: string;
    avatar: string;
    role: string;
    createdAt: string;
  }>;
}

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch('/api/admin/dashboard', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          setData(json);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return <LoadingState>Loading dashboard...</LoadingState>;
  }

  const stats = data?.stats || { totalUsers: 0, totalGames: 0, totalScores: 0, totalDongeng: 0 };
  const qrisStats = data?.qrisStats || { total: 0, paid: 0, pending: 0, expired: 0, failed: 0, revenue: 0, successRate: 0 };
  const recentUsers = data?.recentUsers || [];
  
  const formatCurrency = (amount: number) => `Rp ${amount.toLocaleString('id-ID')}`;

  return (
    <>
      <PageHeader>
        <Title>Dashboard</Title>
        <Subtitle>Selamat datang di Admin Panel Kindergame</Subtitle>
      </PageHeader>

      <StatsGrid>
        <StatCard $color="#2DBCAF">
          <StatIcon $bg="rgba(45, 188, 175, 0.1)">👥</StatIcon>
          <StatInfo>
            <StatValue>{stats.totalUsers.toLocaleString()}</StatValue>
            <StatLabel>Total Pengguna</StatLabel>
          </StatInfo>
        </StatCard>

        <StatCard $color="#3b82f6">
          <StatIcon $bg="rgba(59, 130, 246, 0.1)">🎮</StatIcon>
          <StatInfo>
            <StatValue>{stats.totalGames.toLocaleString()}</StatValue>
            <StatLabel>Total Games</StatLabel>
          </StatInfo>
        </StatCard>

        <StatCard $color="#f59e0b">
          <StatIcon $bg="rgba(245, 158, 11, 0.1)">🏆</StatIcon>
          <StatInfo>
            <StatValue>{stats.totalScores.toLocaleString()}</StatValue>
            <StatLabel>Total Skor</StatLabel>
          </StatInfo>
        </StatCard>

        <StatCard $color="#8b5cf6">
          <StatIcon $bg="rgba(139, 92, 246, 0.1)">📖</StatIcon>
          <StatInfo>
            <StatValue>{stats.totalDongeng.toLocaleString()}</StatValue>
            <StatLabel>Total Dongeng</StatLabel>
          </StatInfo>
        </StatCard>
      </StatsGrid>

      {/* QRIS Stats */}
      <Section style={{ marginBottom: '24px' }}>
        <SectionHeader>
          <SectionTitle>💳 Statistik QRIS</SectionTitle>
        </SectionHeader>
        <StatsGrid style={{ marginBottom: 0 }}>
          <StatCard $color="#3b82f6">
            <StatIcon $bg="rgba(59, 130, 246, 0.1)">📊</StatIcon>
            <StatInfo>
              <StatValue>{qrisStats.total}</StatValue>
              <StatLabel>Total QRIS Dibuat</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard $color="#22c55e">
            <StatIcon $bg="rgba(34, 197, 94, 0.1)">✅</StatIcon>
            <StatInfo>
              <StatValue>{qrisStats.paid}</StatValue>
              <StatLabel>Berhasil ({qrisStats.successRate}%)</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard $color="#f59e0b">
            <StatIcon $bg="rgba(245, 158, 11, 0.1)">⏳</StatIcon>
            <StatInfo>
              <StatValue>{qrisStats.pending}</StatValue>
              <StatLabel>Pending</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard $color="#2DBCAF">
            <StatIcon $bg="rgba(45, 188, 175, 0.1)">💰</StatIcon>
            <StatInfo>
              <StatValue>{formatCurrency(qrisStats.revenue)}</StatValue>
              <StatLabel>Total Revenue</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsGrid>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Pengguna Terbaru</SectionTitle>
        </SectionHeader>
        <UserList>
          {recentUsers.length === 0 ? (
            <div style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
              Belum ada pengguna
            </div>
          ) : (
            recentUsers.map((user) => (
              <UserItem key={user._id}>
                <UserAvatar 
                  src={user.avatar || '/assets/images/default-avatar.png'} 
                  alt={user.name || 'User'} 
                />
                <UserInfo>
                  <UserName>{user.name || 'No name'}</UserName>
                  <UserEmail>{user.email}</UserEmail>
                </UserInfo>
                <RoleBadge $role={user.role || 'ENDUSER'}>
                  {user.role || 'ENDUSER'}
                </RoleBadge>
              </UserItem>
            ))
          )}
        </UserList>
      </Section>
    </>
  );
};

export default DashboardPage;
